import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdminAuth } from './AdminAuthContext';
import { useAdminTheme } from './AdminThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import {
  ArrowLeftRight,
  Search,
  Filter,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  Eye,
  Building2,
  RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Transactions = () => {
  const { canReviewTransactions } = useAdminAuth();
  const { isDarkMode } = useAdminTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [adminNote, setAdminNote] = useState('');

  useEffect(() => { fetchTransactions(); }, [statusFilter]);

  useEffect(() => {
    const status = searchParams.get('status');
    if (status && status !== statusFilter) setStatusFilter(status);
  }, [searchParams]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      params.append('limit', '100');
      const response = await axios.get(`${API}/admin/transactions?${params}`, { withCredentials: true });
      setTransactions(response.data);
    } catch (error) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactionDetails = async (transactionId) => {
    setDetailsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/transactions/${transactionId}`, { withCredentials: true });
      setTransactionDetails(response.data);
    } catch (error) {
      toast.error('Failed to load transaction details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const openDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setDetailsOpen(true);
    fetchTransactionDetails(transaction.transaction_id);
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) return;
    setActionLoading(true);
    try {
      await axios.patch(`${API}/admin/transactions/${selectedTransaction.transaction_id}`, {
        status: newStatus,
        admin_note: adminNote || null
      }, { withCredentials: true });
      toast.success('Transaction status updated');
      setStatusModalOpen(false);
      setNewStatus('');
      setAdminNote('');
      fetchTransactions();
      if (detailsOpen) fetchTransactionDetails(selectedTransaction.transaction_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update transaction');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFilterChange = (value) => {
    setStatusFilter(value);
    if (value === 'all') searchParams.delete('status');
    else searchParams.set('status', value);
    setSearchParams(searchParams);
  };

  const filteredTransactions = transactions.filter(tx => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return tx.transaction_id.toLowerCase().includes(query) ||
      tx.merchant_name?.toLowerCase().includes(query) ||
      tx.description?.toLowerCase().includes(query);
  });

  const getStatusStyle = (status) => {
    switch (status) {
      case 'confirmed': return 'text-green-600 bg-green-50';
      case 'pending': return 'text-amber-600 bg-amber-50';
      case 'failed': return 'text-red-600 bg-red-50';
      case 'manual_review': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <CheckCircle2 className="w-3 h-3" />;
      case 'pending': return <Clock className="w-3 h-3" />;
      case 'failed': return <XCircle className="w-3 h-3" />;
      case 'manual_review': return <AlertTriangle className="w-3 h-3" />;
      default: return null;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US').format(amount);

  return (
    <div data-testid="admin-transactions-page" className="animate-fadeIn">
      <Toaster richColors position="top-right" />
      
      {/* Header */}
      <div className="mb-6">
        <h1 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Transactions</h1>
        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>View and manage all platform transactions</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 group">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors duration-200 group-focus-within:text-teal-500 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
          <Input
            placeholder="Search transactions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`pl-9 h-9 text-sm transition-all duration-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}`}
          />
        </div>
        <Select value={statusFilter} onValueChange={handleFilterChange}>
          <SelectTrigger className={`w-full sm:w-40 h-9 text-sm transition-all duration-200 hover:border-gray-300 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}`}>
            <Filter className={`w-4 h-4 mr-2 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className={isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="manual_review">Manual Review</SelectItem>
          </SelectContent>
        </Select>
        <Button 
          variant="outline" 
          className={`h-9 text-sm transition-all duration-200 hover:border-teal-500 hover:text-teal-600 active:scale-95 ${isDarkMode ? 'border-slate-700 text-slate-300' : 'border-gray-200'}`}
          onClick={fetchTransactions}
        >
          <RefreshCw className="w-4 h-4 mr-1 transition-transform duration-300 hover:rotate-180" /> Refresh
        </Button>
      </div>

      {/* Transactions List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
        </div>
      ) : filteredTransactions.length === 0 ? (
        <div className={`text-center py-12 rounded-xl border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'}`}>
          <ArrowLeftRight className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
          <h3 className={`font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>No transactions found</h3>
          <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className={`rounded-xl border shadow-sm overflow-hidden ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-100'}`}>
          <table className="w-full text-sm">
            <thead className={`border-b ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-100'}`}>
              <tr>
                <th className={`text-left font-medium px-4 py-3 ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>Transaction</th>
                <th className={`text-left font-medium px-4 py-3 ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>Merchant</th>
                <th className={`text-left font-medium px-4 py-3 ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>Amount</th>
                <th className={`text-left font-medium px-4 py-3 ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>Status</th>
                <th className={`text-left font-medium px-4 py-3 ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>Network</th>
                <th className={`text-left font-medium px-4 py-3 ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>Date</th>
                <th className={`text-right font-medium px-4 py-3 ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-50'}`}>
              {filteredTransactions.map((tx, idx) => (
                <tr 
                  key={tx.transaction_id} 
                  className={`transition-all duration-300 cursor-pointer ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gradient-to-r hover:from-transparent hover:via-teal-50/50 hover:to-transparent'}`}
                  style={{ animationDelay: `${idx * 30}ms` }}
                >
                  <td className="px-4 py-3">
                    <div>
                      <p className={`font-mono text-xs ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{tx.transaction_id}</p>
                      <p className={`text-xs truncate max-w-[180px] ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>{tx.description}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 group">
                      <Building2 className={`w-4 h-4 transition-transform duration-200 group-hover:scale-110 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
                      <span className={isDarkMode ? 'text-slate-200' : 'text-gray-700'}>{tx.merchant_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>LRD {formatCurrency(tx.amount)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-all duration-200 hover:scale-105 ${getStatusStyle(tx.status)}`}>
                      {getStatusIcon(tx.status)} {tx.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded font-medium transition-all duration-200 hover:scale-105 ${
                      tx.network === 'MTN' ? 'bg-amber-50 text-amber-600' : 'bg-orange-50 text-orange-600'
                    }`}>{tx.network}</span>
                  </td>
                  <td className={`px-4 py-3 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{formatDate(tx.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className={`h-8 w-8 p-0 transition-all duration-200 hover:scale-110 active:scale-95 ${isDarkMode ? 'text-slate-400 hover:text-teal-400 hover:bg-slate-700' : 'text-gray-400 hover:text-teal-600 hover:bg-teal-50'}`}
                        onClick={() => openDetails(tx)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Transaction Details Modal */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : 'text-gray-900'}>Transaction Details</DialogTitle>
          </DialogHeader>
          
          {detailsLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
            </div>
          ) : transactionDetails ? (
            <div className="space-y-5">
              {/* Transaction Info */}
              <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className={`font-mono text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{transactionDetails.transaction?.transaction_id}</p>
                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{transactionDetails.transaction?.description}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getStatusStyle(transactionDetails.transaction?.status)}`}>
                    {getStatusIcon(transactionDetails.transaction?.status)} {transactionDetails.transaction?.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Amount</span>
                    <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>LRD {formatCurrency(transactionDetails.transaction?.amount)}</p>
                  </div>
                  <div>
                    <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Reference</span>
                    <p className={isDarkMode ? 'text-white' : 'text-gray-900'}>{transactionDetails.transaction?.reference}</p>
                  </div>
                  <div>
                    <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Network</span>
                    <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded font-medium ${
                      transactionDetails.transaction?.network === 'MTN' ? 'bg-amber-50 text-amber-600' : 'bg-orange-50 text-orange-600'
                    }`}>{transactionDetails.transaction?.network}</span>
                  </div>
                  <div>
                    <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Created At</span>
                    <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formatDate(transactionDetails.transaction?.created_at)}</p>
                  </div>
                </div>
              </div>

              {/* Merchant Info */}
              <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
                <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>Merchant</h4>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{transactionDetails.merchant?.business_name}</p>
                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{transactionDetails.merchant?.email}</p>
                  </div>
                  {transactionDetails.merchant?.is_active ? (
                    <span className="ml-auto inline-flex items-center gap-1 text-green-600 bg-green-50 px-2 py-0.5 rounded text-xs font-medium">
                      <CheckCircle2 className="w-3 h-3" /> Active
                    </span>
                  ) : (
                    <span className="ml-auto inline-flex items-center gap-1 text-red-600 bg-red-50 px-2 py-0.5 rounded text-xs font-medium">
                      <XCircle className="w-3 h-3" /> Suspended
                    </span>
                  )}
                </div>
              </div>

              {/* Update Status Action */}
              {canReviewTransactions() && (
                <div className={`pt-4 border-t ${isDarkMode ? 'border-slate-600' : ''}`}>
                  <Button className="w-full bg-teal-500 hover:bg-teal-600" onClick={() => { setNewStatus(transactionDetails.transaction?.status); setStatusModalOpen(true); }}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Update Status
                  </Button>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Update Status Modal */}
      <Dialog open={statusModalOpen} onOpenChange={setStatusModalOpen}>
        <DialogContent className={isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : 'text-gray-900'}>Update Transaction Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className={`text-sm mb-2 block ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>New Status</label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className={`w-full ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'}`}>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent className={isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="manual_review">Manual Review</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={`text-sm mb-2 block ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Admin Note (optional)</label>
              <Textarea 
                placeholder="Add a note..." 
                value={adminNote} 
                onChange={(e) => setAdminNote(e.target.value)} 
                className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setStatusModalOpen(false)} className={isDarkMode ? 'text-slate-400' : ''}>Cancel</Button>
            <Button onClick={handleStatusUpdate} disabled={!newStatus || actionLoading} className="bg-teal-500 hover:bg-teal-600">
              {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Transactions;
